import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Button, List, Card, Tag, Modal, Form, Input, Select,
  message, Popconfirm, Avatar, Spin, Empty, Row, Col,
} from 'antd';
import {
  UserAddOutlined, EditOutlined, DeleteOutlined, TeamOutlined,
  PhoneOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getSubUsersApi, createSubUserApi, editSubUserApi, removeSubUserApi,
} from '../../api/subusers';
import { SUPPORTED_LANGS } from '../../utils/constants';
import ScopePickerModal from './ScopePickerModal';

const { Title, Text } = Typography;

const ROLES = [
  { value: 'manager', label: 'Manager' },
  { value: 'admin',   label: 'Admin'   },
  { value: 'normal',  label: 'Normal'  },
  { value: 'viewer',  label: 'Viewer'  },
];

const ROLE_COLORS = { manager: 'purple', admin: 'blue', normal: 'green', viewer: 'orange' };

const NEEDS_SCOPE = (role) => role === 'normal' || role === 'viewer';

export default function UserAccess() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add / edit form modal
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Scope picker
  const [scopeOpen, setScopeOpen] = useState(false);
  const [pendingScope, setPendingScope] = useState(null);   // scope to submit
  const [pendingFormVals, setPendingFormVals] = useState(null); // form values saved before scope picker opens

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSubUsersApi();
      setUsers(res.data?.users || []);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Open add form ───────────────────────────────────────────────────
  const openAdd = () => {
    setEditingUser(null);
    setSelectedRole('normal');
    setPendingScope(null);
    setPendingFormVals(null);
    form.resetFields();
    form.setFieldsValue({ country_code: '+91', lang: 'en', role: 'normal' });
    setFormOpen(true);
  };

  // ── Open edit (scope picker directly for normal/viewer, else form) ──
  const openEdit = (u) => {
    setEditingUser(u);
    setSelectedRole(u.role);
    setPendingScope(null);
    setPendingFormVals(null);
    form.resetFields();
    form.setFieldsValue({ role: u.role });
    setFormOpen(true);
  };

  // ── Form submit ─────────────────────────────────────────────────────
  const handleFormNext = async () => {
    try {
      const values = await form.validateFields();

      if (NEEDS_SCOPE(values.role)) {
        // Save form values and open scope picker
        setPendingFormVals(values);
        setFormOpen(false);
        setScopeOpen(true);
      } else {
        // Manager / Admin — no scope needed, save immediately
        setSubmitting(true);
        try {
          if (editingUser) {
            await editSubUserApi({ user_id: editingUser.id, role: values.role });
          } else {
            await createSubUserApi({
              name: values.name, mobile: values.mobile,
              country_code: values.country_code, role: values.role,
              lang: values.lang, password: values.password,
            });
          }
          message.success(editingUser ? 'User updated!' : 'User added!');
          setFormOpen(false);
          loadUsers();
        } catch (err) {
          message.error(err?.response?.data?.message || 'Something went wrong');
        } finally {
          setSubmitting(false);
        }
      }
    } catch {
      // validation errors shown inline
    }
  };

  // ── Scope picker save ───────────────────────────────────────────────
  const handleScopePickerSave = async (scope) => {
    setScopeOpen(false);
    setSubmitting(true);
    try {
      if (editingUser) {
        const role = pendingFormVals?.role ?? editingUser.role;
        await editSubUserApi({ user_id: editingUser.id, role, scope });
      } else {
        await createSubUserApi({
          name: pendingFormVals.name,
          mobile: pendingFormVals.mobile,
          country_code: pendingFormVals.country_code,
          role: pendingFormVals.role,
          lang: pendingFormVals.lang,
          password: pendingFormVals.password,
          scope,
        });
      }
      message.success(editingUser ? 'User updated!' : 'User added!');
      loadUsers();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
      setPendingScope(null);
      setPendingFormVals(null);
      setEditingUser(null);
    }
  };

  // Go back from scope picker to form
  const handleScopePickerCancel = () => {
    setScopeOpen(false);
    setFormOpen(true);
  };

  // ── Remove user ─────────────────────────────────────────────────────
  const handleRemove = async (u) => {
    try {
      await removeSubUserApi({ user_id: u.id, mobile: u.mobile });
      message.success('User removed!');
      loadUsers();
    } catch {
      message.error('Failed to remove user');
    }
  };

  const isEditingNeedsScope = editingUser && NEEDS_SCOPE(selectedRole);
  const formOkLabel = NEEDS_SCOPE(selectedRole) ? 'Next: Assign Access →' : (editingUser ? t('save') : t('add_user'));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={4} style={{ color: '#1a6b2a', margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          {t('user_access')}
        </Title>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
          onClick={openAdd}
        >
          {t('add_user')}
        </Button>
      </div>

      <Spin spinning={loading}>
        {!loading && users.length === 0 ? (
          <Empty description={t('no_users_yet')} style={{ marginTop: 60 }} />
        ) : (
          <List
            dataSource={users}
            renderItem={(u) => (
              <List.Item style={{ padding: 0, marginBottom: 12 }}>
                <Card style={{ width: '100%', borderRadius: 10 }} bodyStyle={{ padding: '14px 18px' }}>
                  <Row align="middle" gutter={12} wrap={false}>
                    <Col flex="none">
                      <Avatar size={44} style={{ background: '#1a6b2a', fontSize: 18 }}>
                        {u.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    </Col>
                    <Col flex="auto" style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: 15 }}>{u.name}</Text>
                        <Tag color={ROLE_COLORS[u.role] || 'default'} style={{ margin: 0 }}>
                          {u.role}
                        </Tag>
                      </div>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        <PhoneOutlined style={{ marginRight: 4 }} />
                        {u.country_code} {u.mobile}
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        {u.scope?.length > 0 ? (
                          u.scope.map((s) => (
                            <Tag key={s.farm_id} style={{ marginBottom: 2 }}>{s.farm_name}</Tag>
                          ))
                        ) : (
                          <Tag color="green">Full Access</Tag>
                        )}
                      </div>
                    </Col>
                    <Col flex="none">
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(u)}>
                          {t('edit')}
                        </Button>
                        <Popconfirm
                          title={`${t('remove_user')} ${u.name}?`}
                          description={t('remove_user_confirm')}
                          onConfirm={() => handleRemove(u)}
                          okText={t('remove')}
                          okButtonProps={{ danger: true }}
                          cancelText={t('cancel')}
                        >
                          <Button icon={<DeleteOutlined />} size="small" danger>
                            {t('remove')}
                          </Button>
                        </Popconfirm>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </List.Item>
            )}
          />
        )}
      </Spin>

      {/* Add / Edit form modal */}
      <Modal
        open={formOpen}
        title={editingUser ? `${t('edit_access')} — ${editingUser.name}` : t('add_user')}
        onCancel={() => setFormOpen(false)}
        onOk={handleFormNext}
        okText={formOkLabel}
        cancelText={t('cancel')}
        confirmLoading={submitting}
        width={520}
        okButtonProps={{ style: { background: '#1a6b2a', borderColor: '#1a6b2a' } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          {!editingUser && (
            <>
              <Form.Item label={t('name')} name="name" rules={[{ required: true, message: 'Enter name' }]}>
                <Input placeholder="Full name" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item label={t('country_code')} name="country_code" rules={[{ required: true }]}>
                    <Input placeholder="+91" />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item label={t('mobile')} name="mobile" rules={[{ required: true, message: 'Enter mobile' }]}>
                    <Input placeholder="Mobile number" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label={t('otp_password')} name="password" rules={[{ required: true, message: 'Enter password' }]}>
                    <Input.Password placeholder="Password" maxLength={6} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={t('language')} name="lang" rules={[{ required: true }]}>
                    <Select options={SUPPORTED_LANGS.map((l) => ({ value: l.code, label: l.label }))} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item label={t('role')} name="role" rules={[{ required: true }]}>
            <Select
              options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
              onChange={(v) => setSelectedRole(v)}
            />
          </Form.Item>

          {NEEDS_SCOPE(selectedRole) && (
            <div style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 6,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#555',
            }}>
              <ApartmentOutlined style={{ color: '#52c41a', fontSize: 16 }} />
              {isEditingNeedsScope && editingUser?.scope?.length > 0
                ? `${editingUser.scope.length} farm(s) currently assigned. Click "${formOkLabel}" to update.`
                : `Click "${formOkLabel}" to assign farm / plot / crop access.`
              }
            </div>
          )}
        </Form>
      </Modal>

      {/* Scope picker modal */}
      <ScopePickerModal
        open={scopeOpen}
        initialScope={editingUser?.scope ?? null}
        onSave={handleScopePickerSave}
        onCancel={handleScopePickerCancel}
      />
    </div>
  );
}
